import { Integration } from './integration.model';

export type KendoDialogSettings = {
  ID: string;
  url: string;
  height: number;
  width: number;
  draggable?: boolean;
  scrollable?: boolean;
};

export type MaximizerModule = {
  id: string;
  title: string;
  overrideView?: {
    integrationArgs: {
      integration: Integration;
    };
  };
  image?: string;
  visible?: string;
  external?: string;
  externalUrl?: string;
  isMarketing?: string;
  followingWindowTabs: {
    id: string;
    title: string;
  }[];
  followingWindowIcons: FollowingWindowIcon[];
};

export type FollowingWindowIcon = {
  id: string;
  title: string;
  WebAPIURL: string;
  application: string;
  environments: {
    id: string;
    value: string;
  }[];
  IdentityToken: string;
  url: string;
};

export const FollowingTabEventName = [
  'parentRecordChange',
  'parentSelectionChange',
  'parentRecordUpdate',
  'resize',
  'panelCollapse',
  'panelExpand',
  'focus',
  'blur',
] as const;

export type FollowingTabEvents = (typeof FollowingTabEventName)[number];

export type FollowingTabEventArgs = {
  data: {
    key?: string;
    selection?: string[];
    width?: number;
    height?: number;
  };
};

export interface MaximizerFollowingTab {
  on: (event: string, callback: (args: FollowingTabEventArgs) => void) => void;
  start: () => void;
}

declare global {
  interface Window {
    maxAppPath: string;
    headerData: {
      leadModuleIdentityToken: string;
      userTrackAlias: string;
      featureClientId: string;
      smartFill: string;
      IsCrmLive: string;
      hasCustomerHealthServiceUser: string;
      dateTimeFormatType: 'short' | 'long';
      timeFormat: string;
      yeardateformat: string;
      longDateFormatWithoutWeekday: string;
      dataCenterID: string;
      customerNumber: string;
      version?: string;
      ferretUrl: string;
      usedLicenses: string;
      userPhotoUrl: string;
      userMobileLink: string;
      userAdminURL: string;
    };
    modulesData: {
      modules: MaximizerModule[];
      shortcutsEnabled?: string;
    };
    $resources: { [Key: string]: string };
    $page: {
      MaxKeyAccess: {
        DisableModuleCommand: () => void;
        EnableModuleCommand: () => void;
        DisableInsertCommand: () => void;
        EnableInsertCommand: () => void;
        DisableEditCommand: () => void;
        EnableEditCommand: () => void;
      };
      ModuleSwitcher: {
        currentModuleId: string;
        setCurrentModule: (id: string, url: string) => void;
      };
      addTagToOpenDialog: (tag: string, args: unknown) => void;
      showDialogPage?: (show: boolean, source?: string) => void;
      addTagToSelector: (
        tag: string,
        selector: string,
        tagStyleCss?: Record<string, string>,
        args?: unknown,
        onAction?: (detail: unknown) => void,
      ) => void;
    };
    MaxShowKendoModal: {
      open: (
        settings: KendoDialogSettings,
        closeWithResult: (result: unknown) => void,
        onClose: () => void,
      ) => void;
    };
    MainGridUpdate: {
      ReplaceEntries: (keys: string[]) => void;
    };
    maximizerIdentity: UserInfo;
  }
  namespace maximizer {
    class FollowingTab implements MaximizerFollowingTab {
      constructor();
      on(event: string, callback: (args: FollowingTabEventArgs) => void): void;
      start(): void;
    }
  }
  class MaximizerDataRequest {
    constructor();
    RequestToken: void;
  }
  class MaxCustomAction {
    constructor(elementId: string, apiRequest: MaximizerDataRequest);
    Action(detail: unknown): void;
  }
}

export interface UserSettings {
  is_group_view_enabled?: boolean;
}

export interface UserInfo {
  // Required properties for MaximizerIdentity (must always be provided)
  // All string values should be in lowercase
  alias: string | null; // null only for on-prem
  application: string;
  customer_id: string;
  database_id: string;
  platform_type: string;
  user_login: string;
  workspace_id: string | null; // null only for on-prem

  // Optional properties that may or may not be present
  database_name?: string | null;
  datacenter_id?: string;
  edition?: string;
  email?: string | null;
  enabled_users?: number;
  group_name?: string;
  is_admin?: boolean;
  is_trial?: boolean;
  language?: string;
  roles?: string;
  segments?: string;
  subscription_end_date?: string;
  subscription_start_date?: string;
  total_licenses?: number;
  user_name?: string | null;
  vertical?: string | null;
  settings?: UserSettings;
}
